import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";

// material-ui
import makeStyles from "@material-ui/styles/makeStyles";
import Card from "@material-ui/core/Card";
import Grid from "@material-ui/core/Grid";
import CircularProgress from "@material-ui/core/CircularProgress";
import CardContent from "@material-ui/core/CardContent";
import Divider from "@material-ui/core/Divider";

// perfect scrollbar
import "react-perfect-scrollbar/dist/css/styles.css";
import PerfectScrollbar from "react-perfect-scrollbar";

//icon
import SaveIcon from "@material-ui/icons/Save";

// jaza soft
import {
  PageHeader,
  WithReduxForm,
  BackButton,
  PageFooter,
  Button,
  TextInput, 
  BooleanInput,
  showNotification,
  required as jsRequired,
} from "jazasoft";
import { required } from "@jazasoft/mui-table";

// helper
import useMutation from "../../../hooks/useMutation";
import Forbidden from "../../../components/Forbidden";
import hasPrivilege from "../../../utils/hasPrivilege";

import { ResourceId, Scope } from "../../../utils/privilege";
import MuiTable from "../../../components/MuiTable";
import CardHeader from "../../../components/CardHeader";
import useDeepMemo from "../../../hooks/useDeepMemo";

import { isEmpty } from "../../../utils/helpers";
import useQuery from "../../../hooks/useQuery";

const useStyles = makeStyles(theme => ({
  root: {
    padding: "1.5em",
  },
  mrTop: {
    marginTop: "1.5em",
  },
  pageFooter: {
    "& > button": {
      marginRight: theme.spacing(2),
    },
  },
}));

const ApiKeyCreate = props => {
  const classes = useStyles();
  const { i18nKey } = props;

  const { data: apiList = [] } = useQuery({ url: "apiKeys/apis" });

  const formValues = useSelector(state => state.form && state.form["record-form"] && state.form["record-form"]?.values);

  // hooks
  const dispatch = useDispatch();
  const history = useHistory();
  const [mutate, { loading: saving }] = useMutation();

  const onSubmit = (values, _, onSubmitComplete) => {
    const urlScopeMap = values.reduce(
      (acc, e) => ({
        ...acc,
        [`${e.resourceId}-${e.url}`]: acc[`${e.resourceId}-${e.url}`]
          ? [...acc[`${e.resourceId}-${e.url}`], e.scopeAction]
          : [e.scopeAction],
      }),
      {}
    );
    const apis = Object.keys(urlScopeMap).map(key => ({
      url: key.split("-")[1],
      resourceId: key.split("-")[0],
      scopeActionList: urlScopeMap[key],
    }));

    const data = {
      ...formValues,
      apis: JSON.stringify(apis),
    };

    const options = {
      url: "apiKeys",
      method: "post",
      data,
    };
    mutate(options, {
      onSuccess: data => {
        dispatch(showNotification("Api Key saved successfully."));
        history.push(`/integration/apiKeys/${data.id}/view`);
        onSubmitComplete && onSubmitComplete(values);
      },
    });
  };

  let ffHandleSubmit;
  const handleSubmitRef = submit => {
    ffHandleSubmit = submit;
  };

  const columns = useDeepMemo(() => {
    let columnList = [
      {
        dataKey: "resourceId",
        title: "Resource",
        inputType: "select-input",
        choices: apiList.map(e => ({ id: e.resourceId, name: e.resourceId })),
        validate: [required()],
      },
      {
        dataKey: "url",
        title: "Url",
        inputType: "select-input",
        choices: ({ row }) => {
          const api = apiList.find(api => api.resourceId === row?.resourceId);
          return api?.urlList?.map(url => ({ id: url, name: url })) || [];
        },
        validate: [required()],
      },
      {
        dataKey: "scopeAction",
        title: "ScopeAction",
        inputType: "text-input",
        validate: [required()],
      },
    ];
    return columnList;
  }, [apiList]);

  if (!hasPrivilege(props.roles, props.hasAccess, ResourceId.API_KEY, Scope.WRITE)) {
    return <Forbidden history={props.history} />;
  }

  return (
    <div className={classes.root}>
      <PageHeader title="Create Api Key" />

      <WithReduxForm initialValues={{ active: true }}>
        {({ handleSubmit }) => (
          <div>
            <Card>
              <CardHeader title="Basic Details" />
              <Divider />
              <CardContent>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={3}>
                    <TextInput source="appId" i18nKey={i18nKey} validate={[jsRequired()]} fullWidth />
                  </Grid>
                  <Grid item xs={12} sm={3} style={{ display: "flex", alignItems: "center" }}>
                    <BooleanInput source="active" i18nKey={i18nKey} fullWidth />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            <Card className={classes.mrTop}>
              <CardHeader title="APIs" />
              <Divider />

              <PerfectScrollbar>
                <MuiTable
                  columns={columns}
                  rows={[{ active: true }]}
                  component="div"
                  editable
                  editing
                  enableRowAddition
                  showEditableActions
                  rowAddCount={1}
                  onRowAdd={(rows, rowIdx) => (!isEmpty(rowIdx) ? rows[rowIdx] : rows[rows?.length - 1])}
                  handleSubmitRef={handleSubmitRef}
                  onSubmit={onSubmit}
                  fontSize={16}
                />
              </PerfectScrollbar>
            </Card>

            <PageFooter className={classes.pageFooter}>
              <Button
                label="Save"
                color="primary"
                variant="contained"
                disabled={saving}
                onClick={event => {
                  handleSubmit(() => {})();
                  ffHandleSubmit(event);
                }}>
                <SaveIcon />
                {saving && <CircularProgress size={20} thickness={2} />}
              </Button>
              <BackButton label="Back" variant="outlined" />
            </PageFooter>
          </div>
        )}
      </WithReduxForm>
    </div>
  );
};

export default ApiKeyCreate;
